import { AlertTriangle, Plus } from 'lucide-react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';

interface ContentSettings {
  documentTitle: string;
  customerAddress: {
    enabled: boolean;
    content: string;
  };
  referenceBlock: {
    enabled: boolean;
    items: {
      accountNumber: boolean;
      currencyCode: boolean;
      paymentTerms: boolean;
      statementDate: boolean;
    };
  };
  introText: {
    enabled: boolean;
    content: string;
  };
  paymentInstructions: {
    enabled: boolean;
    content: string;
  };
  closingText: {
    enabled: boolean;
    content: string;
  };
}

interface ContentTabProps {
  settings: ContentSettings;
  onChange: (settings: ContentSettings) => void;
  templateType: 'blank' | 'formal' | 'simple' | 'detailed';
}

const CATEGORY_LABELS: Record<string, string> = {
  customer: 'Customer Information',
  company: 'Company Information',
  payment: 'Payment Information',
  transaction: 'Transaction Details',
};

function VariablePanel({
  fieldName,
  variables,
  getFieldValue,
  toggleVariable,
}: Readonly<{
  fieldName: string;
  variables: Record<string, { label: string; value: string }[]>;
  getFieldValue: (fieldName: string) => string;
  toggleVariable: (fieldName: string, variable: string) => void;
}>) {
  const currentValue = getFieldValue(fieldName);
  return (
    <div className="border rounded-lg p-4 bg-muted/50 space-y-3">
      <p className="text-sm font-medium">Insert Variable</p>
      {Object.entries(variables).map(([category, vars]) => (
        <div key={category} className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground capitalize">
            {CATEGORY_LABELS[category] ?? category}
          </p>
          <div className="grid grid-cols-2 gap-2">
            {vars.map((variable) => {
              const isActive = currentValue.includes(`<<${variable.value}>>`);
              return (
                <Button
                  key={variable.value}
                  variant={isActive ? 'default' : 'outline'}
                  size="sm"
                  className="justify-between text-xs h-auto py-2"
                  onClick={() => toggleVariable(fieldName, variable.value)}
                >
                  <span>{variable.label}</span>
                </Button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

const CUSTOMER_ADDRESS_VARIABLES: Record<string, { label: string; value: string }[]> = {
  customer: [
    { label: 'Customer Name', value: 'customerName' },
    { label: 'Address Line', value: 'customerAddressLine' },
    { label: 'City', value: 'customerCity' },
    { label: 'County', value: 'customerCounty' },
    { label: 'Postcode', value: 'customerPostcode' },
    { label: 'Country', value: 'customerCountry' },
  ],
};

const CONTENT_VARIABLES: Record<string, { label: string; value: string }[]> = {
  customer: [
    { label: 'Customer Name', value: 'customerName' },
    { label: 'Account Number', value: 'accountNumber' },
    { label: 'Customer Email', value: 'customerEmail' },
    { label: 'Customer Phone', value: 'customerPhone' },
  ],
  company: [
    { label: 'Company Name', value: 'companyName' },
    { label: 'Company Email', value: 'companyEmail' },
    { label: 'Company Phone', value: 'companyPhone' },
    { label: 'Comp. Address', value: 'companyAddress' },
  ],
  payment: [
    { label: 'Bank Name', value: 'companyBankName' },
    { label: 'Sort Code', value: 'sortCode' },
    { label: 'Bank A/c Number', value: 'bankAccountNumber' },
    { label: 'IBAN Number', value: 'ibanNumber' },
    { label: 'SWIFT/BIC Code', value: 'swiftBicCode' },
    { label: 'Payment Terms', value: 'paymentTerms' },
  ],
  transaction: [
    { label: 'Statement Date', value: 'statementDate' },
    { label: 'Total Outstanding', value: 'totalOutstanding' },
    { label: 'Total Overdue', value: 'totalOverdue' },
  ],
};

const ALL_ALLOWED_KEYS: Set<string> = new Set(
  [...Object.values(CUSTOMER_ADDRESS_VARIABLES), ...Object.values(CONTENT_VARIABLES)].flat().map((v) => v.value),
);
ALL_ALLOWED_KEYS.add('customerAddressLine1');

function findUnknownVariablesInText(text: string): string[] {
  if (!text) return [];
  const found = new Set<string>();
  const regex = /<<([^>]+)>>/g;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    const key = match[1].trim();
    if (!ALL_ALLOWED_KEYS.has(key)) found.add(key);
  }
  return [...found];
}

function UnknownVariablesWarning({ variables }: Readonly<{ variables: string[] }>) {
  if (variables.length === 0) return null;
  return (
    <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
      <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
      <span>
        Unknown variable{variables.length > 1 ? 's' : ''}: {variables.map((v) => `<<${v}>>`).join(', ')}. Use the Insert
        variable button to add supported variables.
      </span>
    </div>
  );
}

export function ContentTab({ settings, onChange, templateType }: Readonly<ContentTabProps>) {
  const [showVariablePanel, setShowVariablePanel] = useState<string | null>(null);
  const [cursorPositions, setCursorPositions] = useState<Record<string, number>>({});

  const customerAddressRef = useRef<HTMLTextAreaElement>(null);
  const introTextRef = useRef<HTMLTextAreaElement>(null);
  const paymentInstructionsRef = useRef<HTMLTextAreaElement>(null);
  const closingTextRef = useRef<HTMLTextAreaElement>(null);

  const textareaRefs: Record<string, React.RefObject<HTMLTextAreaElement | null>> = {
    customerAddress: customerAddressRef,
    introText: introTextRef,
    paymentInstructions: paymentInstructionsRef,
    closingText: closingTextRef,
  };

  const handleCursorChange = useCallback((fieldName: string, position: number) => {
    setCursorPositions((prev) => ({ ...prev, [fieldName]: position }));
  }, []);

  const handleChange = (updates: Partial<ContentSettings>) => {
    onChange({ ...settings, ...updates });
  };

  const getFieldValue = (fieldName: string): string => {
    if (fieldName === 'documentTitle') return settings.documentTitle;
    if (fieldName === 'customerAddress') return settings.customerAddress.content;
    if (fieldName === 'introText') return settings.introText.content;
    if (fieldName === 'paymentInstructions') return settings.paymentInstructions.content;
    if (fieldName === 'closingText') return settings.closingText.content;
    return '';
  };

  const toggleVariable = (fieldName: string, variable: string) => {
    const variableTag = `<<${variable}>>`;
    const current = getFieldValue(fieldName);
    const isPresent = current.includes(variableTag);

    let updated: string;
    if (isPresent) {
      const removalIndex = current.indexOf(variableTag);
      updated = current
        .replace(new RegExp(`<<${variable}>>`, 'g'), '')
        .replace(/ {2,}/g, ' ')
        .trim();
      if (removalIndex >= 0) {
        setCursorPositions((prev) => ({ ...prev, [fieldName]: Math.min(removalIndex, updated.length) }));
      }
    } else {
      const cursorPos = cursorPositions[fieldName];

      if (cursorPos !== undefined && cursorPos <= current.length) {
        const before = current.substring(0, cursorPos);
        const after = current.substring(cursorPos);
        updated = before + variableTag + after;

        // Restore focus and set cursor after inserted tag
        const ref = textareaRefs[fieldName];
        if (ref?.current) {
          const newCursorPos = cursorPos + variableTag.length;
          setTimeout(() => {
            ref.current?.focus();
            ref.current?.setSelectionRange(newCursorPos, newCursorPos);
          }, 0);
          setCursorPositions((prev) => ({ ...prev, [fieldName]: newCursorPos }));
        }
      } else {
        updated = (current + ' ' + variableTag).trim();
      }
    }

    if (fieldName === 'documentTitle') {
      handleChange({ documentTitle: updated });
    } else if (fieldName === 'customerAddress') {
      handleChange({
        customerAddress: { ...settings.customerAddress, content: updated },
      });
    } else if (fieldName === 'introText') {
      handleChange({ introText: { ...settings.introText, content: updated } });
    } else if (fieldName === 'paymentInstructions') {
      handleChange({
        paymentInstructions: {
          ...settings.paymentInstructions,
          content: updated,
        },
      });
    } else if (fieldName === 'closingText') {
      handleChange({
        closingText: { ...settings.closingText, content: updated },
      });
    }
  };

  const unknownVars = useMemo(
    () => ({
      customerAddress: findUnknownVariablesInText(settings.customerAddress.content),
      introText: findUnknownVariablesInText(settings.introText.content),
      paymentInstructions: findUnknownVariablesInText(settings.paymentInstructions.content),
      closingText: findUnknownVariablesInText(settings.closingText.content),
    }),
    [
      settings.customerAddress.content,
      settings.introText.content,
      settings.paymentInstructions.content,
      settings.closingText.content,
    ],
  );

  return (
    <div className="space-y-4 p-4">
      {/* Panel 1: Document Title */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">Document Title</CardTitle>
          <CardDescription>Appears prominently at the top of the document</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <Input
            id="documentTitle"
            value={settings.documentTitle}
            onChange={(e) => handleChange({ documentTitle: e.target.value })}
            maxLength={60}
            placeholder="Statement of Account"
          />
          <div className="flex items-center justify-end">
            <span className="text-xs text-muted-foreground">{settings.documentTitle.length} / 60</span>
          </div>
        </CardContent>
      </Card>

      {/* Panel 2: Customer Address Block */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-sm font-semibold">Customer Address Block</CardTitle>
              <CardDescription>
                Displays the customer's address on the document for postal or formal use
              </CardDescription>
            </div>
            <Switch
              id="customerAddress"
              checked={settings.customerAddress.enabled}
              onCheckedChange={(checked) =>
                handleChange({
                  customerAddress: {
                    ...settings.customerAddress,
                    enabled: checked,
                    content:
                      checked && !settings.customerAddress.content
                        ? '<<customerName>>\n<<customerAddressLine>>\n<<customerCity>>\n<<customerCounty>>\n<<customerPostcode>>\n<<customerCountry>>'
                        : settings.customerAddress.content,
                  },
                })
              }
            />
          </div>
        </CardHeader>
        {settings.customerAddress.enabled && (
          <CardContent className="space-y-2">
            <Label htmlFor="customerAddressContent">Address content</Label>
            <Textarea
              id="customerAddressContent"
              ref={customerAddressRef}
              value={settings.customerAddress.content}
              onChange={(e) =>
                handleChange({
                  customerAddress: {
                    ...settings.customerAddress,
                    content: e.target.value,
                  },
                })
              }
              onSelect={(e) => handleCursorChange('customerAddress', e.currentTarget.selectionStart)}
              onBlur={(e) => handleCursorChange('customerAddress', e.currentTarget.selectionStart)}
              maxLength={600}
              rows={6}
              placeholder={
                '<<customerName>>\n<<customerAddressLine>>\n<<customerCity>>\n<<customerCounty>>\n<<customerPostcode>>\n<<customerCountry>>'
              }
            />
            <div className="flex items-center justify-between">
              <Button
                variant="link"
                size="sm"
                className="h-auto p-0 text-xs"
                onClick={() => setShowVariablePanel(showVariablePanel === 'customerAddress' ? null : 'customerAddress')}
              >
                <Plus className="h-3 w-3 mr-1" />
                Insert variable
              </Button>
              <span className="text-xs text-muted-foreground">{settings.customerAddress.content.length} / 600</span>
            </div>
            {showVariablePanel === 'customerAddress' && (
              <VariablePanel
                fieldName="customerAddress"
                variables={CUSTOMER_ADDRESS_VARIABLES}
                getFieldValue={getFieldValue}
                toggleVariable={toggleVariable}
              />
            )}
            <UnknownVariablesWarning variables={unknownVars.customerAddress} />
          </CardContent>
        )}
      </Card>

      {/* Panel 3: Introductory Text */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-sm font-semibold">Introductory Text</CardTitle>
              <CardDescription>Optional message shown above the transaction table</CardDescription>
            </div>
            <Switch
              id="introText"
              checked={settings.introText.enabled}
              onCheckedChange={(checked) =>
                handleChange({
                  introText: {
                    ...settings.introText,
                    enabled: checked,
                    content:
                      checked && !settings.introText.content
                        ? 'Please find below your statement of account as of <<statementDate>>. If you have any queries please contact us at <<companyEmail>>.'
                        : settings.introText.content,
                  },
                })
              }
            />
          </div>
        </CardHeader>
        {settings.introText.enabled && (
          <CardContent className="space-y-2">
            <Label htmlFor="introTextContent">Introductory message</Label>
            <Textarea
              id="introTextContent"
              ref={introTextRef}
              value={settings.introText.content}
              onChange={(e) =>
                handleChange({
                  introText: { ...settings.introText, content: e.target.value },
                })
              }
              onSelect={(e) => handleCursorChange('introText', e.currentTarget.selectionStart)}
              onBlur={(e) => handleCursorChange('introText', e.currentTarget.selectionStart)}
              maxLength={500}
              rows={4}
              placeholder="Please find below your statement of account as of <<statementDate>>. If you have any queries please contact us at <<companyEmail>>."
            />
            <div className="flex items-center justify-between">
              <Button
                variant="link"
                size="sm"
                className="h-auto p-0 text-xs"
                onClick={() => setShowVariablePanel(showVariablePanel === 'introText' ? null : 'introText')}
              >
                <Plus className="h-3 w-3 mr-1" />
                Insert variable
              </Button>
              <span className="text-xs text-muted-foreground">{settings.introText.content.length} / 500</span>
            </div>
            {showVariablePanel === 'introText' && (
              <VariablePanel
                fieldName="introText"
                variables={CONTENT_VARIABLES}
                getFieldValue={getFieldValue}
                toggleVariable={toggleVariable}
              />
            )}
            <UnknownVariablesWarning variables={unknownVars.introText} />
          </CardContent>
        )}
      </Card>

      {/* Panel 4: Reference Block */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold">Reference Block</CardTitle>
            <Switch
              id="referenceBlock"
              checked={settings.referenceBlock.enabled}
              onCheckedChange={(checked) =>
                handleChange({
                  referenceBlock: {
                    ...settings.referenceBlock,
                    enabled: checked,
                  },
                })
              }
            />
          </div>
        </CardHeader>
        {settings.referenceBlock.enabled && (
          <CardContent className="space-y-3 pt-0">
            {[
              {
                key: 'accountNumber',
                label: 'Account Number',
                variable: '<<accountNumber>>',
              },
              {
                key: 'currencyCode',
                label: 'Currency Code',
                variable: '<<currencyCode>>',
              },
              {
                key: 'paymentTerms',
                label: 'Payment Terms',
                variable: '<<paymentTerms>>',
              },
              {
                key: 'statementDate',
                label: 'Statement Date',
                variable: '<<statementDate>>',
              },
            ].map((item) => (
              <div key={item.key} className="flex items-center space-x-2">
                <Checkbox
                  id={item.key}
                  checked={settings.referenceBlock.items[item.key as keyof typeof settings.referenceBlock.items]}
                  onCheckedChange={(checked) =>
                    handleChange({
                      referenceBlock: {
                        ...settings.referenceBlock,
                        items: {
                          ...settings.referenceBlock.items,
                          [item.key]: checked,
                        },
                      },
                    })
                  }
                />
                <Label htmlFor={item.key} className="text-sm font-normal flex items-center gap-2">
                  {item.label}
                  <span className="text-xs font-mono text-muted-foreground">{item.variable}</span>
                </Label>
              </div>
            ))}
          </CardContent>
        )}
      </Card>

      {/* Panel 5: Payment Instructions */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-sm font-semibold">Payment Instructions</CardTitle>
              <CardDescription>
                Appears below the transaction table. Use this to provide bank details and payment guidance.
              </CardDescription>
            </div>
            <Switch
              id="paymentInstructions"
              checked={settings.paymentInstructions.enabled}
              onCheckedChange={(checked) =>
                handleChange({
                  paymentInstructions: {
                    ...settings.paymentInstructions,
                    enabled: checked,
                  },
                })
              }
            />
          </div>
        </CardHeader>
        {settings.paymentInstructions.enabled && (
          <CardContent className="space-y-2">
            <Label htmlFor="paymentInstructionsContent">Payment instructions</Label>
            <Textarea
              id="paymentInstructionsContent"
              ref={paymentInstructionsRef}
              value={settings.paymentInstructions.content}
              onChange={(e) =>
                handleChange({
                  paymentInstructions: {
                    ...settings.paymentInstructions,
                    content: e.target.value,
                  },
                })
              }
              onSelect={(e) => handleCursorChange('paymentInstructions', e.currentTarget.selectionStart)}
              onBlur={(e) => handleCursorChange('paymentInstructions', e.currentTarget.selectionStart)}
              maxLength={600}
              rows={5}
              placeholder="Please make payment to <<companyBankName>>, Sort Code <<sortCode>>, Account Number <<bankAccountNumber>>. Please use your account number <<accountNumber>> as the payment reference."
            />
            <div className="flex items-center justify-between">
              <Button
                variant="link"
                size="sm"
                className="h-auto p-0 text-xs"
                onClick={() =>
                  setShowVariablePanel(showVariablePanel === 'paymentInstructions' ? null : 'paymentInstructions')
                }
              >
                <Plus className="h-3 w-3 mr-1" />
                Insert variable
              </Button>
              <span className="text-xs text-muted-foreground">{settings.paymentInstructions.content.length} / 600</span>
            </div>
            {showVariablePanel === 'paymentInstructions' && (
              <VariablePanel
                fieldName="paymentInstructions"
                variables={CONTENT_VARIABLES}
                getFieldValue={getFieldValue}
                toggleVariable={toggleVariable}
              />
            )}
            <UnknownVariablesWarning variables={unknownVars.paymentInstructions} />
          </CardContent>
        )}
      </Card>

      {/* Panel 6: Closing Text */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-sm font-semibold">Closing Text</CardTitle>
              <CardDescription>Optional message shown at the bottom of the document above the footer</CardDescription>
            </div>
            <Switch
              id="closingText"
              checked={settings.closingText.enabled}
              onCheckedChange={(checked) =>
                handleChange({
                  closingText: { ...settings.closingText, enabled: checked },
                })
              }
            />
          </div>
        </CardHeader>
        {settings.closingText.enabled && (
          <CardContent className="space-y-2">
            <Label htmlFor="closingTextContent">Closing message</Label>
            <Textarea
              id="closingTextContent"
              ref={closingTextRef}
              value={settings.closingText.content}
              onChange={(e) =>
                handleChange({
                  closingText: {
                    ...settings.closingText,
                    content: e.target.value,
                  },
                })
              }
              onSelect={(e) => handleCursorChange('closingText', e.currentTarget.selectionStart)}
              onBlur={(e) => handleCursorChange('closingText', e.currentTarget.selectionStart)}
              maxLength={400}
              rows={4}
              placeholder="Thank you for your continued business. If you have any questions regarding this statement please contact us at <<companyEmail>> or <<companyPhone>>."
            />
            <div className="flex items-center justify-between">
              <Button
                variant="link"
                size="sm"
                className="h-auto p-0 text-xs"
                onClick={() => setShowVariablePanel(showVariablePanel === 'closingText' ? null : 'closingText')}
              >
                <Plus className="h-3 w-3 mr-1" />
                Insert variable
              </Button>
              <span className="text-xs text-muted-foreground">{settings.closingText.content.length} / 400</span>
            </div>
            {showVariablePanel === 'closingText' && (
              <VariablePanel
                fieldName="closingText"
                variables={CONTENT_VARIABLES}
                getFieldValue={getFieldValue}
                toggleVariable={toggleVariable}
              />
            )}
            <UnknownVariablesWarning variables={unknownVars.closingText} />
          </CardContent>
        )}
      </Card>
    </div>
  );
}
